/**
 * The Dayax mark. "Dayax" is Somali for moon: a moon-gold crescent and one
 * star on a night-sky tile.
 */
import { cn } from "@/lib/utils";
import { useId, type CSSProperties } from "react";

export const NIGHT_TOP = "#23254D";
export const NIGHT_BOTTOM = "#33388A";
export const MOON_GOLD = "#F4D98C";

/** The app icon: crescent on a night tile. */
export function Logo({ className }: { className?: string }) {
  const id = useId();
  return (
    <svg viewBox="0 0 32 32" className={cn("shrink-0", className)} aria-hidden>
      <defs>
        <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={NIGHT_TOP} />
          <stop offset="1" stopColor={NIGHT_BOTTOM} />
        </linearGradient>
        <mask id={`${id}-cut`}>
          <rect width="32" height="32" fill="white" />
          <circle cx="19.6" cy="12.6" r="7.4" fill="black" />
        </mask>
      </defs>
      <rect width="32" height="32" rx="8" fill={`url(#${id}-sky)`} />
      <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" fill="none" stroke="white" strokeOpacity="0.1" />
      <circle cx="14.6" cy="17.2" r="8.6" fill={MOON_GOLD} mask={`url(#${id}-cut)`} />
      <path d="M24.2 6.2l.62 1.68 1.68.62-1.68.62-.62 1.68-.62-1.68-1.68-.62 1.68-.62z" fill={MOON_GOLD} />
    </svg>
  );
}

/** The crescent alone, in the current text colour. */
export function Crescent({ className }: { className?: string }) {
  const id = useId();
  return (
    <svg viewBox="0 0 24 24" className={cn("shrink-0", className)} aria-hidden>
      <mask id={`${id}-cut`}>
        <rect width="24" height="24" fill="white" />
        <circle cx="15.4" cy="8.6" r="7" fill="black" />
      </mask>
      <circle cx="11" cy="13" r="8.5" fill="currentColor" mask={`url(#${id}-cut)`} />
    </svg>
  );
}

/** A moon running through its phases, for loading screens. */
export function MoonPhases({ className }: { className?: string }) {
  const id = useId();
  return (
    <svg viewBox="0 0 40 40" className={cn("shrink-0", className)} aria-hidden>
      <mask id={`${id}-shade`}>
        <rect width="40" height="40" fill="white" />
        <circle cx="20" cy="20" r="13" fill="black" className="dayax-phase-shade" />
      </mask>
      <circle cx="20" cy="20" r="12" fill="var(--moon)" opacity="0.16" />
      <circle cx="20" cy="20" r="12" fill="var(--moon)" mask={`url(#${id}-shade)`} />
    </svg>
  );
}

/** A four-point star in moon gold. */
export function Sparkle({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 10 10" className={cn("shrink-0", className)} style={style} aria-hidden>
      <path d="M5 0l1.1 3.9L10 5l-3.9 1.1L5 10 3.9 6.1 0 5l3.9-1.1z" fill="var(--moon)" />
    </svg>
  );
}

/**
 * Moonlight for the top of a surface: a low moon's rim just over the edge and
 * its glow. Purely decorative; place inside a `relative overflow-hidden` box.
 */
export function MoonGlow({ size = 520, className }: { size?: number; className?: string }) {
  return (
    <div aria-hidden className={cn("pointer-events-none absolute left-1/2 -translate-x-1/2", className)} style={{ top: -size * 0.72, width: size, height: size }}>
      <div className="absolute inset-0 rounded-full opacity-60 dark:opacity-100" style={{ background: "radial-gradient(closest-side, color-mix(in oklch, var(--moon) 24%, transparent), transparent)" }} />
      <div className="absolute inset-[18%] rounded-full border border-[color-mix(in_oklch,var(--moon)_38%,transparent)] dark:border-[color-mix(in_oklch,var(--moon)_22%,transparent)]" />
    </div>
  );
}
